import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Source, MapRef } from 'react-map-gl';
import { Layer, LayerBand, LAYER_ORDER } from './ordered-layer';
import { createMapSourceAtom } from './atoms';
import { MapUserConfigAtom } from './types';
import { usePatternImageZoom } from './add-pattern-images';

type TitleEntityAssetName = string;
interface TitleEntityAssetsLayerProps {
  mapConfigAtom: MapUserConfigAtom;
  excluding?: TitleEntityAssetName[];
  mapRef: MapRef | null;
}

export const TitleEntityAssetsLayer = ({
  mapConfigAtom,
  excluding = [],
  mapRef,
}: TitleEntityAssetsLayerProps) => {
  const entityAssetsSourceAtom = useMemo(
    () => createMapSourceAtom('entities'),
    []
  );
  usePatternImageZoom(mapRef);
  const entityAssetsSource = useAtomValue(entityAssetsSourceAtom);
  const mapConfig = useAtomValue(mapConfigAtom);

  if (!entityAssetsSource) return null;

  const excludedNameFilter = ['!in', 'name', ...excluding];

  return (
    <LayerBand band={LAYER_ORDER.titleEntityAssets}>
      <Source id="entities" type="vector" url={entityAssetsSource}>
        {mapConfig?.titleEntityAssets?.options?.royaltyInterest && (
          <Layer
            id="title-entity-assets-royalty-interest-fill"
            type="fill"
            paint={{
              'fill-color': '#FFFF00',
              'fill-opacity': 0.5,
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'royalty'],
            ]}
          />
        )}
        {mapConfig?.titleEntityAssets?.options?.royaltyInterest && (
          <Layer
            id="title-entity-assets-royalty-interest-outline"
            type="line"
            paint={{
              'line-color': '#000000',
              'line-width': 1,
              'line-opacity': 1,
            }}
            layout={{
              'line-sort-key': ['get', 'z_order'],
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'royalty'],
            ]}
          />
        )}

        {mapConfig?.titleEntityAssets?.options?.workingInterest && (
          <Layer
            id="title-entity-assets-working-interest-fill"
            type="fill"
            paint={{
              'fill-color': '#000000',
              'fill-opacity': 0.7,
              'fill-pattern': 'vertical-lines-pattern',
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'working'],
            ]}
          />
        )}
        {mapConfig?.titleEntityAssets?.options?.workingInterest && (
          <Layer
            id="title-entity-assets-working-interest-outline"
            type="line"
            paint={{
              'line-color': '#000000',
              'line-width': 1,
              'line-opacity': 1,
            }}
            layout={{
              'line-sort-key': ['get', 'z_order'],
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'working'],
            ]}
          />
        )}

        {mapConfig?.titleEntityAssets?.options?.unleased && (
          <Layer
            id="title-entity-assets-unleased-fill"
            type="fill"
            paint={{
              'fill-color': '#000000',
              'fill-opacity': 0.6,
              'fill-pattern': 'horizontal-lines-pattern',
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'unleased'],
            ]}
          />
        )}
        {mapConfig?.titleEntityAssets?.options?.unleased && (
          <Layer
            id="title-entity-assets-unleased-outline"
            type="line"
            paint={{
              'line-color': '#000000',
              'line-width': 1,
              'line-opacity': 1,
            }}
            layout={{
              'line-sort-key': ['get', 'z_order'],
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'unleased'],
            ]}
          />
        )}

        {mapConfig?.titleEntityAssets?.options?.surfaceInterest && (
          <Layer
            id="title-entity-assets-surface-interest-fill"
            type="fill"
            paint={{
              'fill-color': '#000000',
              'fill-opacity': 0.8,
              'fill-pattern': 'diagonal-hatch-pattern',
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'surface'],
            ]}
          />
        )}
        {mapConfig?.titleEntityAssets?.options?.surfaceInterest && (
          <Layer
            id="title-entity-assets-surface-interest-outline"
            type="line"
            paint={{
              'line-color': '#000000',
              'line-width': 1,
              'line-opacity': 1,
            }}
            layout={{
              'line-sort-key': ['get', 'z_order'],
            }}
            source-layer="entities"
            filter={[
              'all',
              excludedNameFilter,
              ['==', 'interest_type', 'surface'],
            ]}
          />
        )}
      </Source>
    </LayerBand>
  );
};
